import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

const PDP_DRESS_NAME_PROPERTY = 'Dress name';
const CUSTOM_PRODUCT_TEMPLATE = 'product.custom-product';
const CUSTOM_CART_PROPERTY = '_custom';
const CUSTOM_PRICE_CENTS_PROPERTY = '_custom_price';
const DRESS_NAME_LS_PREFIX = 'be_dress_name:';
/** 7 days — PDP-only dress name in localStorage; cleared sooner when the variant is in cart. */
const DRESS_NAME_STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** @param {string} productId @param {string} variantId */
function dressNameStorageKey(productId, variantId) {
  if (!productId || !variantId) return '';
  return `${DRESS_NAME_LS_PREFIX}${productId}_${variantId}`;
}

/** @param {string} productId @param {string} variantId */
function clearStoredDressName(productId, variantId) {
  const key = dressNameStorageKey(productId, variantId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * @param {string} productId
 * @param {string} variantId
 * @returns {string} trimmed name or '' (also removes expired / invalid keys)
 */
function readStoredDressName(productId, variantId) {
  const key = dressNameStorageKey(productId, variantId);
  if (!key) return '';
  try {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === '') return '';

    if (raw.startsWith('{')) {
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        localStorage.removeItem(key);
        return '';
      }
      if (!parsed || typeof parsed !== 'object' || typeof parsed.n !== 'string') {
        localStorage.removeItem(key);
        return '';
      }
      const at = typeof parsed.at === 'number' ? parsed.at : 0;
      if (Date.now() - at > DRESS_NAME_STORAGE_TTL_MS) {
        localStorage.removeItem(key);
        return '';
      }
      return parsed.n.trim();
    }

    const legacy = raw.trim();
    if (!legacy) {
      localStorage.removeItem(key);
      return '';
    }
    try {
      localStorage.setItem(key, JSON.stringify({ n: legacy, at: Date.now() }));
    } catch {
      /* ignore */
    }
    return legacy;
  } catch {
    return '';
  }
}

/** @param {string} productId @param {string} variantId @param {string} name */
function writeStoredDressName(productId, variantId, name) {
  const key = dressNameStorageKey(productId, variantId);
  if (!key) return;
  const t = typeof name === 'string' ? name.trim() : '';
  try {
    if (t) localStorage.setItem(key, JSON.stringify({ n: t, at: Date.now() }));
    else localStorage.removeItem(key);
  } catch {
    /* ignore quota / private mode */
  }
}

/** @param {Element} scope */
function getProductVariantIdsFromScope(scope) {
  const formComp = scope.querySelector('product-form-component[data-product-id]');
  if (!(formComp instanceof HTMLElement)) return { productId: '', variantId: '' };
  const pid = formComp.dataset.productId || '';
  const vidEl = formComp.querySelector('[name="id"]');
  const vid = vidEl instanceof HTMLInputElement ? vidEl.value.trim() : '';
  return { productId: pid, variantId: vid };
}

if (typeof window !== 'undefined') {
  window.__beDressNameStorage = {
    read: readStoredDressName,
    write: writeStoredDressName,
    clear: clearStoredDressName,
    key: dressNameStorageKey,
  };
}

function notifyDressNameDisplaySync() {
  try {
    document.dispatchEvent(new CustomEvent('be:dress-name-changed', { bubbles: true }));
  } catch {
    /* ignore */
  }
}

/** @returns {boolean} */
function isCustomProductTemplatePage() {
  const name = /** @type {{ template?: { name?: string } }} */ (window.Theme)?.template?.name || '';
  return name === CUSTOM_PRODUCT_TEMPLATE;
}

/**
 * @param {FormData} formData
 * @param {string} productId
 */
function appendCustomPriceCartProperties(formData, productId) {
  if (!isCustomProductTemplatePage() || !productId) return;

  /** @type {{ display: string, cents: number } | null | undefined} */
  let info = null;
  if (typeof window.__beCustomProductPriceGetForCart === 'function') {
    info = window.__beCustomProductPriceGetForCart(String(productId));
  }

  if (!info?.cents) {
    const priceEl = document.querySelector(
      `product-price[data-product-id="${productId}"][data-custom-price-active="true"]`
    );
    if (priceEl instanceof HTMLElement) {
      const cents = Number(priceEl.dataset.customPriceCents || 0);
      const display = priceEl.dataset.customPrice || '';
      if (cents > 0 && display) info = { display, cents };
    }
  }

  if (!info?.cents) return;

  formData.set(`properties[${CUSTOM_CART_PROPERTY}]`, 'true');
  formData.set(`properties[${CUSTOM_PRICE_CENTS_PROPERTY}]`, String(info.cents));
}

/**
 * Persist configurator URL/storage data on the main line item for draft checkout.
 * @param {FormData} formData
 * @param {string} productId
 */
function appendBridalConfiguratorCartProperties(formData, productId) {
  if (!isCustomProductTemplatePage() || !productId) return;

  const urlParams = /** @type {typeof window.BridalUrlParams | undefined} */ (window.BridalUrlParams);
  if (!urlParams?.resolveConfigurator) return;

  const config = urlParams.resolveConfigurator(String(productId));
  if (!config) return;

  const metaRaw =
    config.productMetaRaw ||
    Object.entries(config.productMeta || {})
      .map(([k, v]) => `${k}=${v}`)
      .join(',');

  if (metaRaw) {
    formData.set('properties[_product_meta]', metaRaw);
  }

  const maxLen = urlParams.SHOPIFY_LINE_PROPERTY_MAX || 255;
  if (config.snapshotUrl && config.snapshotUrl.length <= maxLen) {
    formData.set('properties[_snapshot_url]', config.snapshotUrl);
  }

  if (config.accessoryVariants?.length) {
    formData.set('properties[_accessory_variant_ids]', config.accessoryVariants.join(','));
  }

  const partsRaw =
    config.propertiesRaw ||
    (config.properties && Object.keys(config.properties).length
      ? Object.entries(config.properties)
          .map(([k, v]) => `${k}=${v}`)
          .join(',')
      : '');

  if (partsRaw) {
    formData.set('properties[_product_parts_ids]', partsRaw.slice(0, maxLen));
  }

  // Legacy visible keys — remove so they do not appear on checkout.
  formData.delete('properties[product_parts_ids]');
  formData.delete('properties[product_meta]');
  formData.delete('properties[snapshot_url]');
  formData.delete('properties[custom_dress_name]');
  formData.delete('properties[accessory_variant_ids]');
  formData.delete('properties[custom]');

  urlParams.writeConfiguratorToStorage(String(productId), config);
}

/**
 * PDP: keep the title-row pencil visible; swap tooltip/a11y label for name vs edit.
 * @param {Element} scope
 * @param {string} dressName
 */
function setDressNameTitleEditButtonVisible(scope, dressName) {
  const on = typeof dressName === 'string' && dressName.trim().length > 0;
  scope.querySelectorAll('[data-dress-name-open-edit]').forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.removeAttribute('hidden');
    const addLabel = btn.dataset.dressNameLabelAdd || 'Name your dress';
    const editLabel = btn.dataset.dressNameLabelEdit || 'Edit custom name';
    const label = on ? editLabel : addLabel;
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
  });
}

/**
 * App → PDP: read `custom_dress_name` from the URL, save to localStorage, prefill UI.
 * Sets dressNameFromAppUrlOverride for one cart-sync pass so it wins over cart line when present.
 * @type {{ productId: string; variantId: string; name: string } | null}
 */
let dressNameFromAppUrlOverride = null;

function tryConsumeCustomDressNameFromUrl() {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  const raw = params.get('custom_dress_name');
  if (!raw || !String(raw).trim()) return;

  const mainForm = document.querySelector(
    '#MainContent product-form-component[data-product-id], main product-form-component[data-product-id]'
  );
  if (!(mainForm instanceof HTMLElement)) return;
  if (mainForm.closest('quick-add-component')) return;

  const productId = mainForm.dataset.productId;
  if (!productId) return;

  const variantParam = params.get('variant');
  const variantInput = mainForm.querySelector('input[name="id"]');
  const variantId =
    (variantParam && variantParam.trim()) ||
    (variantInput instanceof HTMLInputElement ? variantInput.value.trim() : '');
  if (!variantId) return;

  const name = String(raw).trim();
  writeStoredDressName(productId, variantId, name);
  dressNameFromAppUrlOverride = { productId: String(productId), variantId: String(variantId), name };

  const scope = mainForm.closest('.shopify-section') || document;
  const dressInput = scope.querySelector('[data-dress-name-line-property]');
  if (dressInput instanceof HTMLInputElement) {
    dressInput.value = name;
  }
  scope.querySelector('.dress-name-popup')?.classList.add('dress-name-popup--named');

  setDressNameTitleEditButtonVisible(scope, name);

  try {
    params.delete('custom_dress_name');
    const qs = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
    );
  } catch {
    /* ignore */
  }

  applyDressNameToCustomizerLinks(scope, name);
  notifyDressNameDisplaySync();
}

/** @param {string} str */
function decodeLineItemPropertyText(str) {
  if (typeof str !== 'string' || !str) return '';
  return str
    .replace(/&amp;#39;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&apos;/g, "'")
    .replace(/&amp;quot;/g, '"')
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;amp;/g, '&');
}

/** @param {Record<string, unknown>} line @param {string} key */
function readCartLineProperty(line, key) {
  const raw = line?.properties;
  if (raw == null) return '';
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const direct = /** @type {Record<string, string>} */ (raw)[key];
    if (typeof direct === 'string') return direct;
  }
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') continue;
      const name = /** @type {{ name?: string }} */ (entry).name;
      const value = /** @type {{ value?: string }} */ (entry).value;
      if (name === key && typeof value === 'string') return value;
    }
  }
  return '';
}

function normalizeDressNameForCompare(name) {
  return typeof name === 'string' ? name.trim().toLowerCase() : '';
}

function getDressNameDuplicateMessage() {
  const t = /** @type {{ translations?: { dress_name_already_exists?: string } }} */ (
    typeof Theme !== 'undefined' ? Theme : undefined
  );
  const m = t?.translations?.dress_name_already_exists;
  return typeof m === 'string' && m.trim() ? m.trim() : 'This name is already in use. Choose a different name.';
}

/**
 * @param {string} excludeProductId
 * @param {string} excludeVariantId
 * @param {string} proposedName
 */
function dressNameConflictInLocalStorage(excludeProductId, excludeVariantId, proposedName) {
  const want = normalizeDressNameForCompare(proposedName);
  if (!want) return false;
  const excludeKey = dressNameStorageKey(String(excludeProductId), String(excludeVariantId));
  if (!excludeKey) return false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(DRESS_NAME_LS_PREFIX) || key === excludeKey) continue;
      const rest = key.slice(DRESS_NAME_LS_PREFIX.length);
      const li = rest.lastIndexOf('_');
      if (li === -1) continue;
      const opid = rest.slice(0, li);
      const ovid = rest.slice(li + 1);
      const stored = readStoredDressName(opid, ovid);
      if (normalizeDressNameForCompare(stored) === want) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * @param {string} excludeProductId
 * @param {string} excludeVariantId
 * @param {string} proposedName
 */
async function dressNameConflictInCart(excludeProductId, excludeVariantId, proposedName) {
  const want = normalizeDressNameForCompare(proposedName);
  if (!want) return false;
  /** @type {{ routes?: { cart_url?: string } }} */
  const theme = typeof Theme !== 'undefined' ? Theme : undefined;
  const cartUrl = theme?.routes?.cart_url;
  if (!cartUrl) return false;
  try {
    const res = await fetch(`${cartUrl}.js`);
    const cart = await res.json();
    const items = /** @type {Record<string, unknown>[]} */ (cart.items || []);
    for (const item of items) {
      if (
        String(item.product_id) === String(excludeProductId) &&
        String(item.variant_id) === String(excludeVariantId)
      ) {
        continue;
      }
      const raw = readCartLineProperty(item, PDP_DRESS_NAME_PROPERTY);
      const rawTrim = typeof raw === 'string' ? raw.trim() : '';
      const decoded = rawTrim ? decodeLineItemPropertyText(rawTrim) : '';
      if (normalizeDressNameForCompare(decoded) === want) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * @param {string} excludeProductId
 * @param {string} excludeVariantId
 * @param {string} proposedName
 */
async function isDressNameAlreadyUsed(excludeProductId, excludeVariantId, proposedName) {
  if (!normalizeDressNameForCompare(proposedName)) return false;
  if (dressNameConflictInLocalStorage(excludeProductId, excludeVariantId, proposedName)) return true;
  if (await dressNameConflictInCart(excludeProductId, excludeVariantId, proposedName)) return true;
  return false;
}

function hideDressNameDuplicateError(popupHost) {
  if (!(popupHost instanceof Element)) return;
  const msg = popupHost.querySelector('[data-dress-name-duplicate-msg]');
  if (msg instanceof HTMLElement) {
    msg.textContent = '';
    msg.classList.add('hidden');
    msg.setAttribute('hidden', '');
  }
}

function showDressNameDuplicateError(popupHost, text) {
  if (!(popupHost instanceof Element)) return;
  const msg = popupHost.querySelector('[data-dress-name-duplicate-msg]');
  if (msg instanceof HTMLElement) {
    msg.textContent = text;
    msg.classList.remove('hidden');
    msg.removeAttribute('hidden');
  }
}

/**
 * Sync `name` query param on customizer links in a PDP section from dress input and/or cart.
 * @param {Element} scope
 * @param {string} [dressNameFromCart]
 */
function applyDressNameToCustomizerLinks(scope, dressNameFromCart) {
  const input = scope.querySelector('[data-dress-name-line-property]');
  const fromInput = input instanceof HTMLInputElement ? input.value.trim() : '';
  const fromCart = typeof dressNameFromCart === 'string' ? dressNameFromCart.trim() : '';
  const { productId, variantId } = getProductVariantIdsFromScope(scope);
  const fromStorage =
    productId && variantId ? readStoredDressName(productId, variantId) : '';
  const nameForUrl = fromInput || fromCart || fromStorage;

  const selector =
    'a.customize-product-button[href], customize-product-component a[href], a[ref="customizeProductButton"][href]';
  scope.querySelectorAll(selector).forEach((node) => {
    if (!(node instanceof HTMLAnchorElement) || !node.href) return;
    try {
      const u = new URL(node.href);
      if (nameForUrl) {
        u.searchParams.set('name', nameForUrl);
        u.searchParams.set('custom_dress_name', nameForUrl);
      } else {
        u.searchParams.delete('name');
        u.searchParams.delete('custom_dress_name');
      }
      node.href = u.toString();
    } catch {
      /* ignore invalid href */
    }
  });
}

let pdpDressNameInputCustomizeBound = false;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let dressNameLocalStorageDebounce;

function bindDressNameCustomizeUrlSync() {
  if (pdpDressNameInputCustomizeBound) return;
  pdpDressNameInputCustomizeBound = true;

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-dress-name-line-property]')) return;
    const section = target.closest('.shopify-section');
    if (!section) return;

    const dupHostEarly = target.closest('dialog-component.dress-name-popup');
    if (dupHostEarly) {
      hideDressNameDuplicateError(dupHostEarly);
      target.removeAttribute('aria-invalid');
    }

    applyDressNameToCustomizerLinks(section, '');

    const dressPopup = section.querySelector('.dress-name-popup');
    if (dressPopup) {
      if (target.value.trim()) dressPopup.classList.add('dress-name-popup--named');
      else dressPopup.classList.remove('dress-name-popup--named');
    }

    setDressNameTitleEditButtonVisible(section, target.value);

    if (dressNameLocalStorageDebounce) clearTimeout(dressNameLocalStorageDebounce);
    dressNameLocalStorageDebounce = setTimeout(() => {
      dressNameLocalStorageDebounce = undefined;
      const { productId, variantId } = getProductVariantIdsFromScope(section);
      if (!productId || !variantId) return;
      void (async () => {
        const v = target.value.trim();
        if (v && (await isDressNameAlreadyUsed(productId, variantId, v))) {
          const host = target.closest('dialog-component.dress-name-popup');
          if (host) {
            showDressNameDuplicateError(host, getDressNameDuplicateMessage());
            target.setAttribute('aria-invalid', 'true');
          }
          return;
        }
        target.removeAttribute('aria-invalid');
        const hostOk = target.closest('dialog-component.dress-name-popup');
        if (hostOk) hideDressNameDuplicateError(hostOk);
        writeStoredDressName(productId, variantId, target.value);
        notifyDressNameDisplaySync();
      })();
    }, 300);
  });
}

/** Tracks product_variant keys that just had add-to-cart; suppresses name restore for one sync cycle. */
const dressNameSuppressRestore = new Set();

let pdpDressTitleCartSyncBound = false;

let dressNameOpenEditBound = false;

let dressNameSaveValidationBound = false;

function bindDressNameSaveValidation() {
  if (dressNameSaveValidationBound) return;
  dressNameSaveValidationBound = true;

  document.addEventListener(
    'click',
    async (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-dress-name-save-close]') : null;
      if (!btn) return;
      const host = btn.closest('dialog-component.dress-name-popup');
      const dialogEl = host?.querySelector?.('dialog');
      if (!(host instanceof HTMLElement) || typeof /** @type {{ closeDialog?: () => Promise<void> }} */ (host).closeDialog !== 'function' || !(dialogEl instanceof HTMLDialogElement) || !dialogEl.open) {
        return;
      }

      const input = host.querySelector('[data-dress-name-line-property]');
      if (!(input instanceof HTMLInputElement)) return;

      const scope = host.closest('.shopify-section');
      if (!scope) return;
      const { productId, variantId } = getProductVariantIdsFromScope(scope);
      if (!productId || !variantId) {
        hideDressNameDuplicateError(host);
        void host.closeDialog();
        return;
      }

      const proposed = input.value.trim();
      if (!proposed) {
        hideDressNameDuplicateError(host);
        input.removeAttribute('aria-invalid');
        writeStoredDressName(productId, variantId, '');
        dressNameFromAppUrlOverride = null;
        applyDressNameToCustomizerLinks(scope, '');
        notifyDressNameDisplaySync();
        setDressNameTitleEditButtonVisible(scope, '');
        scope.querySelector('.dress-name-popup')?.classList.remove('dress-name-popup--named');
        void host.closeDialog();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (await isDressNameAlreadyUsed(productId, variantId, proposed)) {
        showDressNameDuplicateError(host, getDressNameDuplicateMessage());
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }

      hideDressNameDuplicateError(host);
      input.removeAttribute('aria-invalid');
      writeStoredDressName(productId, variantId, proposed);
      dressNameFromAppUrlOverride = { productId: String(productId), variantId: String(variantId), name: proposed };
      applyDressNameToCustomizerLinks(scope, '');
      notifyDressNameDisplaySync();
      setDressNameTitleEditButtonVisible(scope, proposed);
      scope.querySelector('.dress-name-popup')?.classList.add('dress-name-popup--named');
      void host.closeDialog();
    },
    true
  );
}

function bindDressNameOpenEdit() {
  if (dressNameOpenEditBound) return;
  dressNameOpenEditBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    const btn = target instanceof Element ? target.closest('[data-dress-name-open-edit]') : null;
    if (!btn) return;
    const section = btn.closest('.shopify-section');
    if (!section) return;
    const host = section.querySelector('dialog-component.dress-name-popup');
    if (host && typeof /** @type {{ showDialog?: () => void }} */ (host).showDialog === 'function') {
      event.preventDefault();
      host.showDialog();
    }
  });
}

function bindPdpDressTitleCartSync() {
  if (pdpDressTitleCartSyncBound) return;
  pdpDressTitleCartSyncBound = true;

  bindDressNameCustomizeUrlSync();
  bindDressNameOpenEdit();
  bindDressNameSaveValidation();
  void syncPdpDressTitleFromCart();

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (/** @type {{ detail?: { data?: { didError?: boolean } } }} */ (event).detail?.data?.didError) return;
    void syncPdpDressTitleFromCart();
  });
}

async function syncPdpDressTitleFromCart() {
  tryConsumeCustomDressNameFromUrl();

  /** @type {{ routes?: { cart_url?: string } }} */
  const theme = typeof Theme !== 'undefined' ? Theme : undefined;
  const cartUrl = theme?.routes?.cart_url;
  if (!cartUrl) {
    dressNameFromAppUrlOverride = null;
    return;
  }

  let cart;
  try {
    const res = await fetch(`${cartUrl}.js`);
    cart = await res.json();
  } catch {
    dressNameFromAppUrlOverride = null;
    return;
  }

  const items = /** @type {Record<string, unknown>[]} */ (cart.items || []);

  for (const formEl of document.querySelectorAll('product-form-component[data-product-id]')) {
    if (!(formEl instanceof HTMLElement)) continue;

    const scope =
      formEl.closest('dialog') || formEl.closest('.shopify-section') || formEl.closest('quick-add-component');
    if (!scope) continue;

    const anchor = scope.querySelector('a.product_title_pdp');
    if (!anchor) continue;

    const productId = anchor.dataset.pdpProductId;
    const catalogTitle = anchor.dataset.pdpCatalogTitle;
    if (!productId || catalogTitle === undefined) continue;

    if (String(formEl.dataset.productId) !== String(productId)) continue;

    const variantInput = formEl.querySelector('[name="id"]');
    const variantId =
      variantInput instanceof HTMLInputElement && variantInput.value
        ? variantInput.value
        : anchor.dataset.pdpVariantId;

    const line = items.find(
      (item) =>
        String(item.product_id) === String(productId) && String(item.variant_id) === String(variantId)
    );
    const hasActiveOverrideForThis =
      dressNameFromAppUrlOverride &&
      String(productId) === dressNameFromAppUrlOverride.productId &&
      String(variantId) === dressNameFromAppUrlOverride.variantId;
    if (line && !hasActiveOverrideForThis) {
      clearStoredDressName(String(productId), String(variantId));
    }

    const suppressKey = `${productId}_${variantId}`;
    const suppressed = dressNameSuppressRestore.has(suppressKey);
    if (suppressed) {
      dressNameSuppressRestore.delete(suppressKey);
    }

    let dressName = '';
    let dressNameFromCartLine = '';
    if (!suppressed) {
      const raw = line ? readCartLineProperty(line, PDP_DRESS_NAME_PROPERTY) : '';
      const dressNameRaw = typeof raw === 'string' && raw.trim() ? raw.trim() : '';
      dressNameFromCartLine = dressNameRaw ? decodeLineItemPropertyText(dressNameRaw) : '';
      dressName = dressNameFromCartLine;
      if (!line && !dressName) {
        dressName = readStoredDressName(String(productId), String(variantId));
      }
      if (
        dressNameFromAppUrlOverride &&
        String(productId) === dressNameFromAppUrlOverride.productId &&
        String(variantId) === dressNameFromAppUrlOverride.variantId
      ) {
        dressName = dressNameFromAppUrlOverride.name;
      }
    }
    const display = dressName || catalogTitle;

    const textHost = anchor.querySelector('.text-block p');
    if (textHost) textHost.textContent = display;

    const viewLink = scope.querySelector('.view-product-title a');
    if (viewLink) viewLink.textContent = display;

    const dressInput = scope.querySelector('[data-dress-name-line-property]');
    if (dressInput instanceof HTMLInputElement) {
      dressInput.value = dressName;
    }

    const dressPopup = scope.querySelector('.dress-name-popup');
    if (dressPopup) {
      if (dressName) {
        dressPopup.classList.add('dress-name-popup--named');
      } else {
        dressPopup.classList.remove('dress-name-popup--named');
      }
    }

    setDressNameTitleEditButtonVisible(scope, dressName);

    scope.querySelectorAll('[data-save-to-closet-open]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.setAttribute('data-product-title', display);
      if (dressName) {
        node.setAttribute('data-custom-dress-name', dressName);
      } else {
        node.removeAttribute('data-custom-dress-name');
      }
    });

    applyDressNameToCustomizerLinks(scope, dressNameFromCartLine);
  }

  notifyDressNameDisplaySync();
}

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    bindPdpDressTitleCartSync();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const productIdForCart = this.dataset.productId;
    if (productIdForCart) {
      appendCustomPriceCartProperties(formData, String(productIdForCart));
      appendBridalConfiguratorCartProperties(formData, String(productIdForCart));
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          const productIdAtc = this.dataset.productId;
          if (productIdAtc) {
            clearStoredDressName(String(productIdAtc), String(id));
            this.#resetDressNamePageState(String(productIdAtc));
          }

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          this.#addAccessoryFromLocalStorage();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  #addAccessoryFromLocalStorage() {
    if (!isCustomProductTemplatePage()) return;
    const productId = this.dataset.productId;
    if (!productId) return;

    const storageKey = `product_${productId}_url_params`;
    const storedData = localStorage.getItem(storageKey);
    
    if (!storedData) return;

    try {
      const parsedData = JSON.parse(storedData);
      
      let accessoryVariants = [];
      
      if (parsedData.accessoryVariants && Array.isArray(parsedData.accessoryVariants)) {
        accessoryVariants = parsedData.accessoryVariants;
      } else if (parsedData.productMeta && parsedData.productMeta.accessories) {
        accessoryVariants = [parsedData.productMeta.accessories];
      }
      
      if (accessoryVariants.length === 0) return;

      accessoryVariants.forEach((/** @type {string} */ variantId) => {
        const accessoryFormData = new FormData();
        accessoryFormData.append('id', variantId);
        accessoryFormData.append('quantity', '1');

        const fetchCfg = fetchConfig('javascript', { body: accessoryFormData });

        fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        })
          .then((response) => response.json())
          .then((response) => {
            if (!response.status) {
              this.dispatchEvent(
                new CartAddEvent({}, variantId.toString(), {
                  source: 'product-form-component-accessory',
                  itemCount: 1,
                  productId: variantId,
                  sections: response.sections,
                })
              );
            }
          })
          .catch((error) => {
            console.error('Error adding accessory to cart:', error);
          });
      });
    } catch (e) {
      console.error('Error parsing localStorage data for accessories:', e);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * After add-to-cart, reset dress name UI so a subsequent add uses the default name.
   * @param {string} productId
   */
  #resetDressNamePageState(productId) {
    dressNameFromAppUrlOverride = null;

    const scope = this.closest('.shopify-section') || this.closest('dialog') || document;

    const variantInput = this.querySelector('[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement ? variantInput.value.trim() : '';
    if (variantId) {
      dressNameSuppressRestore.add(`${productId}_${variantId}`);
    }

    // Clear all localStorage dress name keys for this product (all variants)
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(DRESS_NAME_LS_PREFIX) && key.includes(productId)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }

    const dressInput = scope.querySelector('[data-dress-name-line-property]');
    if (dressInput instanceof HTMLInputElement) {
      dressInput.value = '';
    }

    const dressPopup = scope.querySelector('.dress-name-popup');
    if (dressPopup) {
      dressPopup.classList.remove('dress-name-popup--named');
    }

    setDressNameTitleEditButtonVisible(scope, '');

    const anchor = scope.querySelector('a.product_title_pdp');
    if (anchor instanceof HTMLElement) {
      const catalogTitle = anchor.dataset.pdpCatalogTitle;
      if (catalogTitle) {
        const textHost = anchor.querySelector('.text-block p');
        if (textHost) textHost.textContent = catalogTitle;
      }
    }

    applyDressNameToCustomizerLinks(scope, '');
    notifyDressNameDisplaySync();
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    void syncPdpDressTitleFromCart();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
